import { useMutation } from "@apollo/client";
import { Alert, MenuItem, Stack, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import {
  EpisodicEpaFilingFieldsFragment,
  WasteEpisodicEventContactMethod,
  WasteEpisodicEventNoticeKind,
  WasteEpisodicEventType,
} from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { ReactNode, useEffect, useState } from "react";
import { parseDateLikeToLocalDateTime, toUtcMidnightIsoFromDateLike } from "util/dates";

import { UPSERT_EPISODIC_EVENT_NOTICE_MUTATION } from "./api";
import { epaFilingKey, EpaFilingKeyInput, sameEpaFilingKey } from "./epaFilingDisplay";
import { EpaFilingCandidate, EpaFilingSuggestions } from "./EpaFilingSuggestions";
import {
  CONTACT_METHOD_LABELS,
  contactTimeliness,
  isFutureNoticeDate,
  plannedFilingTimeliness,
} from "./episodicDisplay";
import { NoticeProofDocument, NoticeProofField, useNoticeProofUpload } from "./noticeProof";

const RECORD_NOTICE_DIALOG_TITLE: Record<WasteEpisodicEventNoticeKind, string> = {
  [WasteEpisodicEventNoticeKind.EpaContact]: "Record EPA contact",
  [WasteEpisodicEventNoticeKind.FormFiledExternally]: "Record external filing",
};

/** Context caption under the title, per notice kind — says what the dialog is
 * for before the user reaches for the date field. The external filing's
 * second line introduces the EPA records offered below as evidence. */
const RECORD_NOTICE_DIALOG_DESCRIPTION: Record<WasteEpisodicEventNoticeKind, ReactNode> = {
  [WasteEpisodicEventNoticeKind.EpaContact]:
    "EPA requires contact by phone, email, or fax within 72 hours of an unplanned event. Record when and how you made contact.",
  [WasteEpisodicEventNoticeKind.FormFiledExternally]: (
    <>
      <span style={{ display: "block" }}>
        Record when the Form 8700-12 was filed outside Encamp.
      </span>
      <span style={{ display: "block" }}>
        You can attach one of the episodic filings we&apos;ve pulled from EPA&apos;s RCRAInfo
        system, or upload a copy of a different filing.
      </span>
    </>
  ),
};

const PROOF_HELPER_TEXT: Record<WasteEpisodicEventNoticeKind, string> = {
  [WasteEpisodicEventNoticeKind.EpaContact]:
    "Attach proof of contact (optional) — e.g. a confirmation email.",
  [WasteEpisodicEventNoticeKind.FormFiledExternally]: "Upload a copy of the filed Form 8700-12",
};

const CONTACT_METHOD_VALUES = new Set<string>(Object.values(WasteEpisodicEventContactMethod));

/** Narrows the raw `TextField` select value back to the enum without an `as`
 * cast — the options rendered below are always drawn from this same enum. */
function isContactMethod(value: string): value is WasteEpisodicEventContactMethod {
  return CONTACT_METHOD_VALUES.has(value);
}

export type RecordNoticeDialogProps = {
  eventId: string;
  /** Drives which fields render: EPA_CONTACT needs a method alongside the
   * date, FORM_FILED_EXTERNALLY only needs the date. */
  kind: WasteEpisodicEventNoticeKind;
  /** The event's start date and type, needed to judge the entered date
   * against the rule the notice is satisfying (see `noticeDateWarning`). */
  eventStartDate: string;
  eventType: WasteEpisodicEventType;
  open: boolean;
  onClose: () => void;
  /** The facility the event belongs to — threads through to
   * `EpaFilingSuggestions` when the user chooses EPA's record as evidence. */
  facilityId: string;
  /** The existing notice's values, when this dialog is reopened to correct
   * an already-recorded notice rather than capture a new one. Applied on
   * every open (see the effect below) rather than once at mount, since the
   * dialog stays mounted across opens with `open` merely toggling. */
  initialDate?: string | null;
  initialMethod?: WasteEpisodicEventContactMethod | null;
  /** The proof already attached to the notice being corrected, if any. */
  initialDocument?: NoticeProofDocument | null;
  /** The EPA filing already attached as evidence, when this dialog is
   * reopened to correct an existing notice. Seeds the selected candidate,
   * mirroring how `initialDate` seeds the date. */
  initialEpaFiling?: EpisodicEpaFilingFieldsFragment | null;
};

/**
 * Non-blocking inline warning for the date entered in this dialog, mirroring
 * `EpaContactSection`/`ExternalFilingSection`'s capture-time nudges. An
 * EPA_CONTACT date is judged against the unplanned-only 72-hour rule
 * regardless of the event's type (the rule itself only ever applies to
 * unplanned events, so a planned event never reaches this dialog for that
 * kind); a FORM_FILED_EXTERNALLY date only carries a deadline for a PLANNED
 * event — an unplanned 8700-12 filing has none, so this always returns
 * `null` for that combination.
 */
function noticeDateWarning(
  kind: WasteEpisodicEventNoticeKind,
  eventType: WasteEpisodicEventType,
  eventStartDate: string,
  noticeDate: string
): string | null {
  if (kind === WasteEpisodicEventNoticeKind.EpaContact) {
    const timeliness = contactTimeliness(eventStartDate, noticeDate);
    if (timeliness === "late") {
      return "This contact was outside the 72-hour window — record it anyway; a late record beats no record.";
    }
    if (timeliness === "beforeEvent") {
      return "This date is before the event's start date — double-check it.";
    }
    return null;
  }
  if (eventType !== WasteEpisodicEventType.Planned) {
    return null;
  }
  const timeliness = plannedFilingTimeliness(eventStartDate, noticeDate);
  return timeliness === "late"
    ? "This filing was less than 30 days before the start date — the notice requirement wasn't met."
    : null;
}

/**
 * The dialog's `date`/`method` form fields, seeded from the notice being
 * edited every time the dialog opens. The dialog stays mounted with `open`
 * merely toggling, so a stale edit from a previous open (or the prior
 * notice's values) must not leak into the next one — reapply the prefill on
 * every `open` flip, mirroring EpisodicEventDialog's open-triggered
 * validation trigger. `date`/`method` are genuinely local form state, only
 * ever seeded from `open` flipping (a prop transition, not a rendered value)
 * and freely edited afterward — not the derived-render-value the
 * no-derived-state lint is meant to catch. The effect deliberately depends on
 * `open` alone: the prefill should only be (re)applied on that transition,
 * not whenever the caller's notice data happens to change while the dialog
 * is already open.
 */
function usePrefilledNoticeFields(
  open: boolean,
  initialDate: string | null | undefined,
  initialMethod: WasteEpisodicEventContactMethod | null | undefined,
  initialEpaFiling: EpisodicEpaFilingFieldsFragment | null | undefined
) {
  const [date, setDate] = useState<DateTime | null>(null);
  const [method, setMethod] = useState<WasteEpisodicEventContactMethod | null>(null);
  // The proof slot's local state: a file picked but not yet uploaded (nothing
  // uploads before save), and whether the already-attached document was
  // explicitly removed. Reset on every open like the prefilled fields.
  const [proofFiles, setProofFiles] = useState<File[]>([]);
  const [proofRemoved, setProofRemoved] = useState(false);
  // The EPA filing chosen as evidence (if any) — seeded like the fields above.
  const [selectedFiling, setSelectedFiling] = useState<EpisodicEpaFilingFieldsFragment | null>(
    null
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setDate(initialDate ? (parseDateLikeToLocalDateTime(initialDate) ?? null) : null);
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setMethod(initialMethod ?? null);
    setProofFiles([]);
    setProofRemoved(false);
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setSelectedFiling(initialEpaFiling ?? null);
    // oxlint-disable-next-line exhaustive-deps
  }, [open]);

  return {
    date,
    setDate,
    method,
    setMethod,
    proofFiles,
    setProofFiles,
    proofRemoved,
    setProofRemoved,
    selectedFiling,
    setSelectedFiling,
  };
}

type NoticeFields = ReturnType<typeof usePrefilledNoticeFields>;

/**
 * Owns the save flow: resolve the proof intent (upload a picked file, detach
 * an explicitly removed one, or leave the field out entirely so an untouched
 * proof is never overwritten by a date/method correction), then upsert the
 * notice with the result.
 */
function useSaveNotice({
  eventId,
  kind,
  fields,
  initialDocument,
  initialEpaFiling,
  onSaved,
}: {
  eventId: string;
  kind: WasteEpisodicEventNoticeKind;
  fields: NoticeFields;
  initialDocument: NoticeProofDocument | null | undefined;
  initialEpaFiling: EpisodicEpaFilingFieldsFragment | null | undefined;
  onSaved: () => void;
}) {
  const alerts = useAlerts();
  const { tenantId } = useTenant();
  const { uploadProof, isUploading } = useNoticeProofUpload();
  const [upsertNotice, { loading }] = useMutation(UPSERT_EPISODIC_EVENT_NOTICE_MUTATION, {
    refetchQueries: ["FacilityEpisodicEvents"],
  });
  const requiresMethod = kind === WasteEpisodicEventNoticeKind.EpaContact;

  // Resolves both evidence slots' tri-state intent: the EPA filing (attached,
  // cleared, or unchanged) and a proof document (uploaded, kept, or
  // detached). Omitting a key leaves that slot untouched server-side, so an
  // untouched slot is never overwritten by a correction to the other one.
  const resolveEvidenceInput = async (): Promise<{
    documentId?: string | null;
    epaFiling?: EpaFilingKeyInput | null;
  }> => {
    const evidence: { documentId?: string | null; epaFiling?: EpaFilingKeyInput | null } = {};
    if (!sameEpaFilingKey(fields.selectedFiling, initialEpaFiling)) {
      evidence.epaFiling = fields.selectedFiling ? epaFilingKey(fields.selectedFiling) : null;
    }
    const file = fields.proofFiles[0];
    if (file) {
      if (!tenantId) {
        throw new Error("Cannot upload without a tenant");
      }
      const uploaded = await uploadProof(file, tenantId);
      evidence.documentId = uploaded.id;
    } else if (initialDocument && fields.proofRemoved) {
      evidence.documentId = null;
    }
    return evidence;
  };

  const handleSave = async () => {
    // A filing chosen from EPA's own record is sent verbatim as the notice
    // date — the server requires it to equal the record's receive date
    // exactly, so this sidesteps any drift from round-tripping through the
    // date picker's local-time DateTime.
    let noticeDate: string | undefined;
    if (fields.selectedFiling) {
      noticeDate = fields.selectedFiling.receiveDate;
    } else if (fields.date) {
      noticeDate = toUtcMidnightIsoFromDateLike(fields.date);
    }
    if (!noticeDate || (requiresMethod && !fields.method) || isFutureNoticeDate(noticeDate)) {
      return;
    }
    let evidenceInput: { documentId?: string | null; epaFiling?: EpaFilingKeyInput | null };
    try {
      evidenceInput = await resolveEvidenceInput();
    } catch {
      alerts.error("Failed to upload the proof document");
      return;
    }
    await upsertNotice({
      variables: {
        input: {
          eventId,
          kind,
          noticeDate,
          method: requiresMethod ? (fields.method ?? undefined) : undefined,
          ...evidenceInput,
        },
      },
      onCompleted: () => {
        alerts.success("Notice recorded");
        onSaved();
      },
      onError: () => {
        alerts.error("An error occurred while recording the notice");
      },
    });
  };

  return { handleSave, isSaving: loading || isUploading };
}

/**
 * Small inline dialog for manually recording a notice that already happened
 * outside Encamp: an EPA contact (date + method) or an externally filed
 * 8700-12 (date only), each with a proof upload — required for the external
 * filing, optional for the contact. `kind` alone decides which fields render.
 *
 * For the external filing, EPA's own candidate records are looked up and
 * shown first, with the upload folded in beneath them; a lone type-matching
 * record is selected on the user's behalf, but only when the notice has no
 * evidence attached yet — a correction to a notice already carrying an
 * upload must never swap its evidence silently.
 */
export function RecordNoticeDialog({
  eventId,
  kind,
  eventStartDate,
  eventType,
  open,
  onClose,
  facilityId,
  initialDate,
  initialMethod,
  initialDocument,
  initialEpaFiling,
}: RecordNoticeDialogProps) {
  const fields = usePrefilledNoticeFields(open, initialDate, initialMethod, initialEpaFiling);
  const {
    date,
    setDate,
    method,
    setMethod,
    proofFiles,
    setProofFiles,
    setProofRemoved,
    selectedFiling,
    setSelectedFiling,
  } = fields;
  const requiresMethod = kind === WasteEpisodicEventNoticeKind.EpaContact;
  const requiresProof = kind === WasteEpisodicEventNoticeKind.FormFiledExternally;
  const keptDocument = initialDocument && !fields.proofRemoved ? initialDocument : null;
  const noticeDate = date ? toUtcMidnightIsoFromDateLike(date) : undefined;
  const isFutureDate = noticeDate != null && isFutureNoticeDate(noticeDate);
  const dateWarning =
    noticeDate != null && !isFutureDate
      ? noticeDateWarning(kind, eventType, eventStartDate, noticeDate)
      : null;

  const resetFields = () => {
    setDate(null);
    setMethod(null);
    setProofFiles([]);
    setProofRemoved(false);
    setSelectedFiling(null);
  };

  const handleClose = () => {
    resetFields();
    onClose();
  };

  const { handleSave, isSaving } = useSaveNotice({
    eventId,
    kind,
    fields,
    initialDocument,
    initialEpaFiling,
    onSaved: handleClose,
  });

  const chooseFiling = ({ filing }: EpaFilingCandidate) => {
    setSelectedFiling(filing);
    setDate(DateTime.fromISO(filing.receiveDate, { zone: "utc" }));
    // A file dropped before choosing the record would otherwise still
    // upload silently on save, alongside the chosen EPA filing.
    setProofFiles([]);
  };
  const clearFiling = () => setSelectedFiling(null);
  const autoSelectSingle = requiresProof && initialDocument == null && initialEpaFiling == null;

  const proofField = (
    <NoticeProofField
      existingDocument={keptDocument}
      files={proofFiles}
      onFilesChange={setProofFiles}
      onRemoveExisting={requiresProof ? undefined : () => setProofRemoved(true)}
      disabled={isSaving}
      helperText={PROOF_HELPER_TEXT[kind]}
    />
  );

  const hasProof = proofFiles.length > 0 || keptDocument != null || selectedFiling != null;
  const canSave =
    date != null &&
    !isFutureDate &&
    (!requiresMethod || method != null) &&
    (!requiresProof || hasProof);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      title={RECORD_NOTICE_DIALOG_TITLE[kind]}
      description={RECORD_NOTICE_DIALOG_DESCRIPTION[kind]}
      primaryActionButtonProps={{
        loading: isSaving,
        onClick: handleSave,
        disabled: !canSave,
      }}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <DatePicker
          label="Date"
          value={date}
          onChange={setDate}
          disabled={selectedFiling != null}
        />
        {isFutureDate && <Alert severity="error">The date can&apos;t be in the future.</Alert>}
        {dateWarning && <Alert severity="warning">{dateWarning}</Alert>}
        {requiresMethod && (
          <TextField
            select
            label="Method"
            value={method ?? ""}
            onChange={(event) => {
              const { value } = event.target;
              if (isContactMethod(value)) {
                setMethod(value);
              }
            }}
          >
            {Object.entries(CONTACT_METHOD_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        )}
        {requiresProof ? (
          <EpaFilingSuggestions
            facilityId={facilityId}
            eventType={eventType}
            startDate={eventStartDate}
            excludeEventId={eventId}
            selectedKey={selectedFiling ? epaFilingKey(selectedFiling) : null}
            autoSelectSingle={autoSelectSingle}
            keepUploadOpen={proofFiles.length > 0 || keptDocument != null}
            onChoose={chooseFiling}
            onClear={clearFiling}
            uploadField={proofField}
            busy={isSaving}
          />
        ) : (
          proofField
        )}
      </Stack>
    </Dialog>
  );
}
